import { useState } from 'react';
import { Link } from 'wouter';
import {
  ArrowLeft,
  Camera,
  Upload,
  Palette,
  CreditCard,
  Bell,
  CheckCircle2,
  Play,
  Phone,
  MessageCircle,
  ChevronRight,
  Info,
  Smartphone,
} from 'lucide-react';
import { SEOHead } from '../components/SEOHead';
import { FooterPixCapture } from '../components/FooterPixCapture';

export default function PixCaptureHelp() {
  const [activeStep, setActiveStep] = useState<number | null>(null);

  const steps = [
    {
      id: 1,
      icon: Camera,
      title: 'Fotografieren',
      description: 'Nutze die integrierte Kamera-App oder wähle Fotos aus deiner Galerie',
      details: [
        'Aktiviere die Wasserwaage für gerade Linien',
        'Nutze HDR für optimale Belichtung',
        'Fotografiere alle Räume systematisch',
        'Achte auf gutes Tageslicht',
      ],
      videoPlaceholder: 'Tutorial: Professionell fotografieren',
    },
    {
      id: 2,
      icon: Upload,
      title: 'Upload starten',
      description: 'Fotos hochladen von iPhone oder Android – automatische Kompression',
      details: [
        'Wähle bis zu 100 Bilder aus',
        'Upload läuft im Hintergrund',
        'Fortschrittsanzeige zeigt Status',
        'Automatische Fehlerbehandlung',
      ],
      videoPlaceholder: 'Tutorial: Upload-Prozess',
    },
    {
      id: 3,
      icon: Palette,
      title: 'Stil auswählen',
      description: 'Wähle deinen Bearbeitungsstil: Natural, Bright, Dramatic',
      details: [
        'Natural: Natürliche Farbkorrektur',
        'Bright: Helle, freundliche Atmosphäre',
        'Dramatic: Kontrastreiche Bearbeitung',
        'Änderung jederzeit möglich',
      ],
      videoPlaceholder: 'Tutorial: Stilauswahl',
    },
    {
      id: 4,
      icon: CreditCard,
      title: 'Bezahlung',
      description: 'Sichere Zahlung per Kreditkarte, PayPal oder SEPA-Lastschrift',
      details: [
        'Preise: 3-5€ pro Bild',
        'Rechnung nach Upload',
        'Sichere Zahlungsabwicklung',
        'Keine Abogebühren',
      ],
      videoPlaceholder: 'Tutorial: Bezahlvorgang',
    },
    {
      id: 5,
      icon: Bell,
      title: 'Status & Benachrichtigungen',
      description: 'Erhalte Push-Benachrichtigungen über den Bearbeitungsstatus',
      details: [
        'Upload abgeschlossen: Sofort',
        'Bearbeitung gestartet: Nach QC',
        'Fertig: Nach 24-48h',
        'Download-Link in App-Galerie',
      ],
      videoPlaceholder: 'Tutorial: Benachrichtigungen',
    },
  ];

  const faqItems = [
    {
      question: 'Welche Geräte werden unterstützt?',
      answer:
        'pixcapture.app funktioniert auf iPhone (iOS 14+) und Android (Android 10+). Die Kamera-Features sind auf beiden Plattformen verfügbar.',
    },
    {
      question: 'Wie lange dauert die Bearbeitung?',
      answer:
        'In der Regel 24-48 Stunden. Bei dringenden Aufträgen bieten wir Express-Bearbeitung innerhalb von 12 Stunden an (Aufpreis 50%).',
    },
    {
      question: 'Kann ich Fotos nachträglich ergänzen?',
      answer:
        'Ja, du kannst jederzeit weitere Fotos zu einem bestehenden Job hochladen, solange dieser noch nicht abgeschlossen ist.',
    },
    {
      question: 'Was passiert bei schlechter Bildqualität?',
      answer:
        'Unser Quality Check Team prüft alle Uploads. Bei technischen Problemen (Unschärfe, Unterbelichtung) erhältst du eine Benachrichtigung mit Hinweisen zur Neuaufnahme.',
    },
    {
      question: 'Wie erhalte ich die bearbeiteten Bilder?',
      answer:
        'Die fertigen Bilder erscheinen in deiner App-Galerie. Du erhältst eine Push-Benachrichtigung und kannst alle Fotos als ZIP herunterladen.',
    },
    {
      question: 'Sind die Fotos rechtlich sicher?',
      answer:
        'Ja, alle Rechte an den Fotos bleiben bei dir. Wir nutzen sie ausschließlich für die Bearbeitung und löschen sie nach 90 Tagen automatisch.',
    },
  ];

  return (
    <div className="min-h-screen bg-[#F9F9F7] flex flex-col">
      <SEOHead
        title="Hilfe & Funktionen – pixcapture.app"
        description="Schritt-für-Schritt-Anleitung zur Nutzung von pixcapture.app"
        path="/help"
      />

      {/* Header */}
      <header className="bg-white border-b border-[#E5E5E5] sticky top-0 z-20">
        <div className="max-w-[1200px] mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link href="/">
                <button className="h-10 w-10 flex items-center justify-center hover:bg-gray-100 rounded">
                  <ArrowLeft className="h-5 w-5" />
                </button>
              </Link>
              <div>
                <h1 className="text-[24px]">Hilfe & Funktionen</h1>
                <p className="text-[14px] text-[#6B7280]">So funktioniert pixcapture.app</p>
              </div>
            </div>

            <Link href="/expert-call">
              <button className="bg-[#74A4EA] text-white hover:opacity-90 px-4 py-2">
                <Phone className="h-5 w-5 mr-2 inline" />
                Experten-Call buchen
              </button>
            </Link>
          </div>
        </div>
      </header>

      <main className="flex-1">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-[#74A4EA] to-[#64BF49] text-white py-16">
          <div className="max-w-[1200px] mx-auto px-6 text-center">
            <Info className="h-16 w-16 mx-auto mb-4 opacity-80" />
            <h2 className="mb-4 text-[36px]">Deine Schritt-für-Schritt-Anleitung</h2>
            <p className="max-w-[700px] mx-auto text-white/90 text-[16px]">
              Von der ersten Aufnahme bis zu den fertigen, bearbeiteten Bildern – wir begleiten dich durch jeden Schritt.
            </p>
          </div>
        </section>

        {/* Steps Section */}
        <section className="py-16 max-w-[1200px] mx-auto px-6">
          <div className="space-y-6">
            {steps.map((step, index) => (
              <div
                key={step.id}
                className={`bg-white border-2 rounded-lg p-8 cursor-pointer transition-all ${
                  activeStep === step.id ? 'border-[#64BF49] shadow-lg' : 'border-[#E5E5E5] hover:border-[#74A4EA]'
                }`}
                onClick={() => setActiveStep(activeStep === step.id ? null : step.id)}
              >
                <div className="flex items-start gap-6">
                  <div
                    className="flex-shrink-0 w-16 h-16 rounded-full flex items-center justify-center"
                    style={{
                      background: activeStep === step.id ? '#64BF49' : '#74A4EA',
                    }}
                  >
                    <step.icon className="h-8 w-8 text-white" />
                  </div>

                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-[20px]">
                        {index + 1}. {step.title}
                      </h3>
                      <ChevronRight
                        className={`h-6 w-6 text-[#6B7280] transition-transform ${
                          activeStep === step.id ? 'rotate-90' : ''
                        }`}
                      />
                    </div>
                    <p className="text-[#6B7280] mb-4 text-[14px]">{step.description}</p>

                    {activeStep === step.id && (
                      <div className="space-y-4 pt-4 border-t border-[#E5E5E5]">
                        <div className="grid md:grid-cols-2 gap-4">
                          <div>
                            <h4 className="text-[14px] mb-3">Details:</h4>
                            <ul className="space-y-2">
                              {step.details.map((detail, idx) => (
                                <li key={idx} className="flex items-start gap-2 text-[#6B7280] text-[13px]">
                                  <CheckCircle2 className="h-5 w-5 text-[#64BF49] flex-shrink-0 mt-0.5" />
                                  <span>{detail}</span>
                                </li>
                              ))}
                            </ul>
                          </div>

                          <div>
                            <div className="bg-[#F0F0F0] rounded-lg aspect-video flex flex-col items-center justify-center gap-3 border-2 border-dashed border-[#E5E5E5]">
                              <Play className="h-12 w-12 text-[#74A4EA]" />
                              <p className="text-[#6B7280] text-[13px]">{step.videoPlaceholder}</p>
                              <button className="px-3 py-1 border border-gray-300 rounded text-sm" disabled>
                                Demnächst verfügbar
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-16 max-w-[1200px] mx-auto px-6">
          <h2 className="text-center mb-12 text-[28px]">Häufig gestellte Fragen</h2>
          <div className="space-y-4">
            {faqItems.map((item, index) => (
              <details key={index} className="bg-white border border-[#E5E5E5] rounded-lg px-6 py-4">
                <summary className="cursor-pointer text-[16px]">{item.question}</summary>
                <p className="text-[#6B7280] text-[14px] mt-2">{item.answer}</p>
              </details>
            ))}
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 bg-[#F0F0F0]">
          <div className="max-w-[800px] mx-auto px-6 text-center">
            <MessageCircle className="h-16 w-16 text-[#74A4EA] mx-auto mb-6" />
            <h2 className="mb-4 text-[28px]">Noch Fragen?</h2>
            <p className="text-[#6B7280] mb-8 text-[16px]">
              Buche ein kostenloses Expertengespräch und lass dich persönlich beraten.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link href="/expert-call">
                <button className="bg-[#74A4EA] text-white hover:opacity-90 px-6 py-3">
                  <Phone className="h-5 w-5 mr-2 inline" />
                  Experten-Call buchen
                </button>
              </Link>
            </div>
          </div>
        </section>
      </main>

      <FooterPixCapture />
    </div>
  );
}
